import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { verifyIdentityUser, fetchUserBillingDetails } from "../../features/auth/authSlice";
import {
  Box,
  Typography,
  CircularProgress,
  Container,
  Paper,
  Snackbar,
  Alert,
  Dialog,
  DialogContent,
  Button
} from "@mui/material";
import {
  CheckCircleOutline,
  CalendarMonth,
  CreditCard,
  InfoOutlined,
  Refresh
} from "@mui/icons-material";
import useThemeColor from "../../utils/useTheme";
import dayjs from "dayjs";

const VerifyIdentity = () => {
  const [searchParams] = useSearchParams();
  const [openSnackbar, setOpenSnackbar] = useState(true);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const is_completed = Boolean(searchParams.get("is_completed"));
  const userId = searchParams.get("userId") || "";
  const sessionId = searchParams.get("sessionId");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const styles = useThemeColor();
  const [openSubscriptionDialog, setOpenSubscriptionDialog] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState({ date: "", amount: "" });
  const [pendingNavigation, setPendingNavigation] = useState(null);

  useEffect(() => {
    if (is_completed && sessionId) {
      try {
        dispatch(verifyIdentityUser({ userId, sessionId })).then(
          async ({ payload }) => {
            if (payload && payload.token) {
              // Facebook CAPI event now handled by backend (authController verifyIdentity)
              const lastVisitedRoute = localStorage.getItem("lastVisitedRoute");
              let route = "/";
              let navState = { state: { is_verified: true } };

              if (!payload?.user?.isActive) {
                const masterclassType = (
                  payload?.user?.trackingData?.masterclassType ||
                  payload?.user?.masterclassType ||
                  payload?.user?.userType ||
                  ""
                ).toLowerCase();

                const targetUserId = payload?.user?.userId || payload?.user?._id || userId;

                if (masterclassType === "coaches" || masterclassType === "coach") {
                  route = `/complete-registeration-coaches-masterclass?userid=${targetUserId}`;
                } else if (masterclassType === "accelerator") {
                  route = `/complete-registeration-accelerator-masterclass?userid=${targetUserId}`;
                } else {
                  route = `/complete-registration?userid=${targetUserId}`;
                }
                navState = {};
              } else if (lastVisitedRoute) {
                route = lastVisitedRoute;
              }

              if (payload?.user?.userType === "Gym" && !payload?.user?.isAdmin) {
                try {
                  const actionResult = await dispatch(fetchUserBillingDetails());
                  const billingRes = actionResult.payload;

                  const currentSub = billingRes?.currentSubscription?.[0];
                  if (currentSub?.interval === "month") {
                    let formattedDate = "your renewal date";
                    const rawDate = billingRes?.nextBillingDate;
                    if (rawDate) {
                      try {
                        const parts = rawDate.split(' ')[0].split('-');
                        if (parts.length === 3) {
                          const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
                          const monthIndex = parseInt(parts[1], 10) - 1;
                          const day = parseInt(parts[2], 10);
                          if (monthIndex >= 0 && monthIndex < 12 && day > 0 && day <= 31) {
                            formattedDate = `${monthNames[monthIndex]} ${day}`;
                          } else {
                            formattedDate = rawDate;
                          }
                        } else {
                          formattedDate = rawDate;
                        }
                      } catch (e) {
                        formattedDate = rawDate;
                      }
                    }

                    setSubscriptionDetails({
                      date: formattedDate,
                      amount: currentSub?.amount ? currentSub.amount / 100 : 39
                    });
                    setPendingNavigation({ route, state: navState });
                    setOpenSubscriptionDialog(true);
                    return; // Stop here, wait for dialog 'OK'
                  }
                } catch (err) {
                  console.error("Failed to fetch billing details on verify: ", err);
                }

              }

              // Standard navigation
              setTimeout(() => {
                navigate(route, navState);
              }, 1000);
            }
          }
        );
        setSnackbarMessage("Verified Successfully!");
        setOpenSnackbar(true);
      } catch (err) {
        setSnackbarMessage("Try Again! Complete your payment first!");
        setOpenSnackbar(true);
        console.error(err);
      }
    } else {
      setSnackbarMessage("Try Again! Complete your payment first!");
      setOpenSnackbar(true);
      navigate("/", { state: { is_verified: false } });
    }
  }, [userId, sessionId, dispatch, is_completed, navigate]);

  const handleAcceptDialog = () => {
    setOpenSubscriptionDialog(false);
    if (pendingNavigation) {
      navigate(pendingNavigation.route, pendingNavigation.state);
    } else {
      navigate("/");
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Snackbar
        open={openSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarMessage?.includes("Success") ? "success" : "error"}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          textAlign: "center",
          backgroundColor: styles.background,
          color: styles.color,
          borderRadius: 3
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column"
          }}
        >
          <CircularProgress sx={{ mb: 3, color: styles.primaryColor }} />
          <Typography variant="h5" component="h2" gutterBottom sx={{ color: styles.color }}>
            Verifying your identity...
          </Typography>
          <Typography variant="body1" sx={{ color: styles.lightBlack }}>
            Please wait while we verify your identity. You will be redirected
            shortly.
          </Typography>
        </Box>
      </Paper>

      {/* Gym Monthly Subscription Popup */}
      <Dialog
        open={openSubscriptionDialog}
        onClose={handleAcceptDialog}
        aria-labelledby="subscription-dialog-title"
        aria-describedby="subscription-dialog-description"
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            p: 0,
            borderRadius: 4,
            overflow: "hidden",
            background: styles.background,
            boxShadow: `0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px ${styles.primaryColor}20`
          }
        }}
      >
        {/* Premium Header */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${styles.primaryColor} 0%, ${styles.primaryColor}CC 50%, ${styles.primaryColor}99 100%)`,
            p: 3,
            textAlign: "center",
            position: "relative",
            overflow: "hidden"
          }}
        >
          {/* Decorative Circles */}
          <Box
            sx={{
              position: "absolute",
              top: -20, right: -20,
              width: 100, height: 100,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.15)",
              filter: "blur(20px)"
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: -30, left: -30,
              width: 120, height: 120,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.1)",
              filter: "blur(25px)"
            }}
          />
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 64,
                height: 64,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.25)",
                backdropFilter: "blur(10px)",
                mb: 1.5,
                boxShadow: "0 8px 32px rgba(0,0,0,0.2)"
              }}
            >
              <CheckCircleOutline sx={{ fontSize: 34, color: "#fff" }} />
            </Box>
            <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700, textShadow: "0 2px 8px rgba(0,0,0,0.2)" }}>
              Subscription Confirmed
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.9)", fontWeight: 500, mt: 0.5 }}>
              Welcome to Three Pillars Method
            </Typography>
          </Box>
        </Box>

        {/* Content */}
        <DialogContent sx={{ p: 3 }}>
          {/* Monthly Badge */}
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', mb: 2.5 }}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
                px: 2, py: 0.75,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${styles.primaryColor}15, ${styles.primaryColor}08)`,
                border: `1px solid ${styles.primaryColor}30`
              }}
            >
              <CreditCard sx={{ fontSize: 18, color: styles.primaryColor }} />
              <Typography variant="caption" sx={{ color: styles.color, fontWeight: 600, fontSize: 12 }}>
                RECURRING MONTHLY PLAN
              </Typography>
            </Box>
          </Box>

          {/* Info Cards */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2.5 }}>
            {/* Subscription Type */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                py: 1.5, px: 2,
                borderRadius: 2.5,
                background: styles.modBackgroundBlocks,
                border: `1px solid ${styles.primaryColor}15`
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 28, height: 28,
                  borderRadius: "50%",
                  background: `linear-gradient(135deg, ${styles.primaryColor}, ${styles.primaryColor}DD)`,
                  flexShrink: 0
                }}
              >
                <Refresh sx={{ fontSize: 16, color: "#fff" }} />
              </Box>
              <Typography variant="body2" sx={{ color: styles.color, fontWeight: 500 }}>
                You are on a reoccurring <strong>Monthly</strong> subscription.
              </Typography>
            </Box>

            {/* Renewal Date */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                py: 1.5, px: 2,
                borderRadius: 2.5,
                background: styles.modBackgroundBlocks,
                border: `1px solid ${styles.primaryColor}15`
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 28, height: 28,
                  borderRadius: "50%",
                  background: `linear-gradient(135deg, ${styles.primaryColor}, ${styles.primaryColor}DD)`,
                  flexShrink: 0
                }}
              >
                <CalendarMonth sx={{ fontSize: 16, color: "#fff" }} />
              </Box>
              <Typography variant="body2" sx={{ color: styles.color, fontWeight: 500 }}>
                You are set to renew on <strong>{subscriptionDetails.date}</strong> for <strong>${subscriptionDetails.amount}</strong>.
              </Typography>
            </Box>

            {/* Cancellation Info */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                py: 1.5, px: 2,
                borderRadius: 2.5,
                background: styles.modBackgroundBlocks,
                border: `1px solid ${styles.primaryColor}15`
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 28, height: 28,
                  borderRadius: "50%",
                  background: `linear-gradient(135deg, ${styles.primaryColor}, ${styles.primaryColor}DD)`,
                  flexShrink: 0
                }}
              >
                <InfoOutlined sx={{ fontSize: 16, color: "#fff" }} />
              </Box>
              <Typography variant="body2" sx={{ color: styles.color, fontWeight: 500 }}>
                You can cancel your subscription any time in the{" "}
                <Box
                  component="span"
                  onClick={() => {
                    setOpenSubscriptionDialog(false);
                    navigate('/manage-billing');
                  }}
                  sx={{
                    color: styles.primaryColor,
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  Billing
                </Box>
                {" "}tab of your profile.
              </Typography>
            </Box>
          </Box>

          {/* OK Button */}
          <Button
            onClick={handleAcceptDialog}
            fullWidth
            variant="contained"
            sx={{
              py: 1.5,
              background: `linear-gradient(135deg, ${styles.primaryColor} 0%, ${styles.primaryColor}DD 100%)`,
              color: "#fff",
              textTransform: "none",
              fontWeight: 700,
              fontSize: 16,
              borderRadius: 3,
              boxShadow: `0 8px 24px -4px ${styles.primaryColor}60`,
              transition: "all 0.3s ease",
              "&:hover": {
                background: `linear-gradient(135deg, ${styles.primaryColor}DD 0%, ${styles.primaryColor} 100%)`,
                transform: "translateY(-2px)",
                boxShadow: `0 12px 32px -4px ${styles.primaryColor}70`
              }
            }}
          >
            OK
          </Button>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default VerifyIdentity;
